import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, Button, CheckBox, Select, Text, TextInput } from "grommet";
import { Add, FormClose } from "grommet-icons";
import { CurrencyInput, ConfirmDialog } from "../../components";
import { useAuth, useDirtyGuard } from "../../context";
import {
    centsToDollars,
    formatCents,
    parseDollarsToCents,
} from "../../utils/money";
import { todayIsoAEST, formatDateAu } from "../../utils/date";
import {
    deleteAllocation,
    getLoanAllocations,
    hardDeleteAllocation,
    restoreAllocation,
    saveLoanAllocations,
} from "../../api/funderAllocation";
import { Panel } from "./Panel";
import { Badge } from "./Badge";
import { ctaButtonProps } from "./ctaButton";

const renderFunderOption = (option, index, options, { active, selected }) => (
    <Box
        pad={{ vertical: "xsmall", horizontal: "small" }}
        background={selected ? "active" : active ? "surface-hover" : undefined}
    >
        <Text color="white">{option.label}</Text>
    </Box>
);

const SummaryBar = ({ currentCents, totalCents }) => {
    const ratio = totalCents > 0 ? Math.min(currentCents / totalCents, 1) : 0;
    const balanced = currentCents === totalCents;
    const over = currentCents > totalCents;
    const barColor = balanced
        ? "status-live"
        : over
          ? "status-critical"
          : "active";

    return (
        <Box gap="xsmall" width="medium">
            <Box direction="row" justify="between">
                <Text weight="bold" size="small">
                    {formatCents(currentCents)} / {formatCents(totalCents)}
                </Text>
                {!balanced && (
                    <Text size="small" color="status-critical">
                        {over ? "over" : "under"} by{" "}
                        {formatCents(Math.abs(totalCents - currentCents))}
                    </Text>
                )}
            </Box>
            <Box
                height="6px"
                round="large"
                background="surface-border"
                overflow="hidden"
            >
                <Box
                    height="100%"
                    width={`${ratio * 100}%`}
                    background={barColor}
                />
            </Box>
        </Box>
    );
};

// These row components are defined at module scope (not inside
// AllocationEditor) deliberately - if they were declared inside the
// component body, each render would create a brand-new function identity,
// and React would unmount/remount the whole row subtree instead of
// reconciling it, dropping focus out of the inputs on every keystroke.

const LiveRow = ({ row, funderName, onReallocate, onDelete }) => (
    <Box
        direction="row"
        align="center"
        gap="small"
        pad="small"
        round="xsmall"
        background="surface"
        border={{ color: "surface-border" }}
    >
        <Badge tone="live">Live</Badge>
        <Box flex>
            <Text weight="bold">{funderName(row.funderId)}</Text>
            <Text size="small" color="dark-5">
                Effective {formatDateAu(row.effectiveDate)}
            </Text>
        </Box>
        <Text weight="bold">{formatCents(row.portionCents)}</Text>
        {onReallocate && (
            <Button
                label="Reallocate"
                size="small"
                onClick={() => onReallocate(row)}
            />
        )}
        <Button
            label="Delete"
            size="small"
            plain
            onClick={() => onDelete(row.id)}
        />
    </Box>
);

const ReplacedRow = ({ row, funderName }) => (
    <Box
        direction="row"
        align="center"
        gap="small"
        pad={{ horizontal: "small" }}
    >
        <Badge tone="neutral">Was</Badge>
        <Text size="small" color="dark-5">
            {funderName(row.funderId)} · {formatCents(row.portionCents)} ·{" "}
            {formatDateAu(row.effectiveDate)}
        </Text>
    </Box>
);

const DraftRow = ({
    row,
    funderOptions,
    onFunderChange,
    onPortionChange,
    onPortionBlur,
    onDateChange,
    onCancel,
}) => (
    <Box
        direction="row"
        align="center"
        gap="small"
        pad="small"
        round="xsmall"
        background="surface-hover"
        border={{ color: "active" }}
    >
        <Badge tone="editing">
            {row.sourcePreviousAllocationId != null ? "Reallocating" : "New"}
        </Badge>
        <Box width="medium">
            <Select
                placeholder="Funder"
                options={funderOptions}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                value={row.funderId}
                onChange={({ value }) => onFunderChange(row.localId, value)}
            >
                {renderFunderOption}
            </Select>
        </Box>
        <Box width="small">
            <CurrencyInput
                value={row.portionInput}
                onChange={(raw) => onPortionChange(row.localId, raw)}
                onBlur={() => onPortionBlur(row.localId)}
            />
        </Box>
        <Box width="small">
            <TextInput
                type="date"
                value={row.effectiveDate}
                onChange={(e) => onDateChange(row.localId, e.target.value)}
            />
        </Box>
        <Button
            icon={<FormClose />}
            label="Cancel"
            size="small"
            a11yTitle="Cancel this reallocation - it has not been saved"
            onClick={() => onCancel(row.localId)}
        />
    </Box>
);

const HistoryRow = ({
    row,
    tone,
    funderName,
    successor,
    isAdmin,
    onDelete,
    onRestore,
    onHardDelete,
}) => (
    <Box direction="row" align="center" gap="small" pad="small" round="xsmall">
        <Badge tone={tone}>
            {tone === "retired" ? "Historical" : "Deleted"}
        </Badge>
        <Box flex>
            <Text>{funderName(row.funderId)}</Text>
            {successor && (
                <Text size="small" color="dark-5">
                    → replaced by {funderName(successor.funderId)}
                </Text>
            )}
        </Box>
        <Text color="dark-5">{formatCents(row.portionCents)}</Text>
        <Text size="small" color="dark-5">
            {formatDateAu(row.effectiveDate)}
        </Text>
        {tone === "retired" && (
            <Button
                label="Delete"
                size="small"
                plain
                onClick={() => onDelete(row.id)}
            />
        )}
        {tone === "deleted" && (
            <Button
                label="Restore"
                size="small"
                onClick={() => onRestore(row.id)}
            />
        )}
        {tone === "deleted" && isAdmin && (
            <Button
                label="Delete permanently"
                size="small"
                color="status-critical"
                onClick={() => onHardDelete(row.id)}
            />
        )}
    </Box>
);

// Reallocating a saved allocation never edits it in place - it's retired
// server-side and replaced by a new row, linked back via the saved item's
// previousAllocationId. The user never sees or picks an allocation id
// directly: clicking "Reallocate" on a live row is what sets that link.
// If two reallocations (or a reallocation and an untouched live row) land
// on the same destination funder, the backend folds them into a single
// row whose predecessorIds lists every row it absorbed - see successorOf
// below, which has to search that list rather than a single id.
const AllocationEditor = ({
    loanId,
    loanAmountCents,
    loanIsActive,
    funders,
    isAdmin,
}) => {
    const { tokens } = useAuth();
    const { setDirty } = useDirtyGuard();

    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const [draftRows, setDraftRows] = useState([]);
    const [replacedIds, setReplacedIds] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const nextLocalId = useRef(0);

    const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
    const [confirmingHardDeleteId, setConfirmingHardDeleteId] = useState(null);

    const load = useCallback(async () => {
        if (!tokens?.idToken) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getLoanAllocations(tokens.idToken, loanId, {
                showDeleted: true,
            });
            setAllocations(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load allocations");
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, [tokens, loanId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setDirty(
            draftRows.length > 0,
            "You have unsaved allocation changes. Leave without saving?",
        );
    }, [draftRows.length, setDirty]);

    const funderName = (id) =>
        funders.find((f) => f.id === id)?.name || `#${id}`;

    const liveRows = useMemo(
        () => allocations.filter((a) => a.isActive && !a.retiredAt),
        [allocations],
    );
    const retiredRows = useMemo(
        () => allocations.filter((a) => a.isActive && a.retiredAt),
        [allocations],
    );
    const deletedRows = useMemo(
        () => allocations.filter((a) => !a.isActive),
        [allocations],
    );
    const successorOf = (row) =>
        allocations.find((a) => a.predecessorIds?.includes(row.id));

    const untouchedTotalCents = liveRows
        .filter((r) => !replacedIds.has(r.id))
        .reduce((sum, r) => sum + r.portionCents, 0);

    const draftRowsWithCents = draftRows.map((r) => ({
        ...r,
        portionCents: parseDollarsToCents(r.portionInput),
    }));
    const draftTotalCents = draftRowsWithCents.reduce(
        (sum, r) => sum + (r.portionCents || 0),
        0,
    );
    const currentTotalCents = untouchedTotalCents + draftTotalCents;
    const needsReallocation =
        liveRows.length > 0 && currentTotalCents !== loanAmountCents;

    const hasInvalidRow = draftRowsWithCents.some(
        (r) =>
            !r.funderId ||
            !r.portionCents ||
            r.portionCents <= 0 ||
            !r.effectiveDate,
    );
    const canSave =
        loanIsActive &&
        draftRows.length > 0 &&
        currentTotalCents === loanAmountCents &&
        !hasInvalidRow;

    const draftsFor = (sourceKey) =>
        draftRows.filter(
            (r) => (r.sourcePreviousAllocationId ?? "new") === sourceKey,
        );

    const addDraftRow = (
        sourcePreviousAllocationId,
        prefillCents,
        prefillFunderId,
    ) => {
        setDraftRows((prev) => [
            ...prev,
            {
                localId: nextLocalId.current++,
                sourcePreviousAllocationId,
                funderId: prefillFunderId ?? null,
                portionInput: String(centsToDollars(prefillCents)),
                effectiveDate: todayIsoAEST(),
            },
        ]);
    };

    const handleAddFirstAllocation = () =>
        addDraftRow(null, loanAmountCents, null);

    const handleReallocate = (liveRow) => {
        setReplacedIds((prev) => new Set(prev).add(liveRow.id));
        addDraftRow(liveRow.id, liveRow.portionCents, liveRow.funderId);
    };

    const handleCancelDraft = (localId) => {
        setDraftRows((prev) => {
            const row = prev.find((r) => r.localId === localId);
            const next = prev.filter((r) => r.localId !== localId);
            if (row?.sourcePreviousAllocationId != null) {
                const stillReferenced = next.some(
                    (r) =>
                        r.sourcePreviousAllocationId ===
                        row.sourcePreviousAllocationId,
                );
                if (!stillReferenced) {
                    setReplacedIds((prevSet) => {
                        const nextSet = new Set(prevSet);
                        nextSet.delete(row.sourcePreviousAllocationId);
                        return nextSet;
                    });
                }
            }
            return next;
        });
    };

    const updateDraftRow = (localId, patch) => {
        setDraftRows((prev) =>
            prev.map((r) => (r.localId === localId ? { ...r, ...patch } : r)),
        );
    };

    // After the user finishes editing a portion, offer the remainder as a
    // new row (same lineage) so they can keep splitting until it balances.
    const handlePortionBlur = (localId) => {
        // Capture state at blur time, but defer the actual row-adding state
        // update to the next tick. Doing it synchronously inside the native
        // 'blur' event handler meant React's DOM update (inserting a new
        // row) landed while the browser was still resolving that same blur/
        // focus transition - which interrupted it, leaving the input
        // needing an explicit re-click to keep typing. Letting the browser
        // finish its own focus handoff first avoids the race.
        const row = draftRows.find((r) => r.localId === localId);
        if (!row) return;
        const total = draftRows.reduce(
            (sum, r) => sum + (parseDollarsToCents(r.portionInput) || 0),
            0,
        );
        const remaining = loanAmountCents - untouchedTotalCents - total;
        const alreadyHasOpenSibling = draftRows.some(
            (r) =>
                r.sourcePreviousAllocationId ===
                    row.sourcePreviousAllocationId &&
                !r.funderId &&
                r.localId !== localId,
        );
        if (remaining > 0 && !alreadyHasOpenSibling) {
            setTimeout(() => {
                addDraftRow(row.sourcePreviousAllocationId, remaining, null);
            }, 0);
        }
    };

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            await saveLoanAllocations(
                tokens.idToken,
                loanId,
                draftRowsWithCents.map((r) => ({
                    funderId: r.funderId,
                    portionCents: r.portionCents,
                    effectiveDate: r.effectiveDate,
                    previousAllocationId: r.sourcePreviousAllocationId,
                })),
            );
            setDraftRows([]);
            setReplacedIds(new Set());
            load();
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to save allocations");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const id = confirmingDeleteId;
        setConfirmingDeleteId(null);
        setError(null);
        try {
            await deleteAllocation(tokens.idToken, id);
            load();
        } catch (e) {
            setError(
                e?.response?.data?.detail || "Failed to delete allocation",
            );
        }
    };

    const handleRestore = async (id) => {
        setError(null);
        try {
            await restoreAllocation(tokens.idToken, id);
            load();
        } catch (e) {
            setError(
                e?.response?.data?.detail || "Failed to restore allocation",
            );
        }
    };

    const handleHardDelete = async () => {
        const id = confirmingHardDeleteId;
        setConfirmingHardDeleteId(null);
        setError(null);
        try {
            await hardDeleteAllocation(tokens.idToken, id);
            load();
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Failed to permanently delete allocation",
            );
        }
    };

    const funderOptions = funders.map((f) => ({ label: f.name, value: f.id }));

    return (
        <Panel>
            <Box direction="row" justify="between" align="center">
                <Text weight="bold" size="large">
                    Allocations
                </Text>
                <CheckBox
                    checked={showHistory}
                    label="Show history"
                    onChange={(e) => setShowHistory(e.target.checked)}
                />
            </Box>

            {!hasLoaded && <Text>Loading…</Text>}
            {error && <Text color="status-critical">{error}</Text>}

            {!loanIsActive && (
                <Box background="surface-hover" pad="small" round="xsmall">
                    <Text size="small" color="dark-5">
                        This loan has been deleted - restore it to add or change
                        allocations.
                    </Text>
                </Box>
            )}

            {hasLoaded &&
                !loading &&
                liveRows.length === 0 &&
                draftRows.length === 0 &&
                loanIsActive && (
                    <Text size="small" color="dark-5">
                        No allocations yet - add the first allocation below.
                    </Text>
                )}
            {hasLoaded && !loading && needsReallocation && (
                <Box background="status-warning" pad="small" round="xsmall">
                    <Text size="small" color="#2b2200">
                        The live allocations don't add up to the loan amount -
                        use "Reallocate" below to update the ones that need to
                        change.
                    </Text>
                </Box>
            )}

            {!loading && (
                <Box gap="xsmall">
                    {liveRows.map((row) =>
                        replacedIds.has(row.id) ? (
                            <Box key={row.id} gap="xsmall">
                                <ReplacedRow
                                    row={row}
                                    funderName={funderName}
                                />
                                {draftsFor(row.id).map((draft) => (
                                    <DraftRow
                                        key={draft.localId}
                                        row={draft}
                                        funderOptions={funderOptions}
                                        onFunderChange={(localId, value) =>
                                            updateDraftRow(localId, {
                                                funderId: value,
                                            })
                                        }
                                        onPortionChange={(localId, raw) =>
                                            updateDraftRow(localId, {
                                                portionInput: raw,
                                            })
                                        }
                                        onPortionBlur={handlePortionBlur}
                                        onDateChange={(localId, date) =>
                                            updateDraftRow(localId, {
                                                effectiveDate: date,
                                            })
                                        }
                                        onCancel={handleCancelDraft}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <LiveRow
                                key={row.id}
                                row={row}
                                funderName={funderName}
                                onReallocate={
                                    loanIsActive ? handleReallocate : undefined
                                }
                                onDelete={(id) => setConfirmingDeleteId(id)}
                            />
                        ),
                    )}
                    {draftsFor("new").map((draft) => (
                        <DraftRow
                            key={draft.localId}
                            row={draft}
                            funderOptions={funderOptions}
                            onFunderChange={(localId, value) =>
                                updateDraftRow(localId, { funderId: value })
                            }
                            onPortionChange={(localId, raw) =>
                                updateDraftRow(localId, { portionInput: raw })
                            }
                            onPortionBlur={handlePortionBlur}
                            onDateChange={(localId, date) =>
                                updateDraftRow(localId, { effectiveDate: date })
                            }
                            onCancel={handleCancelDraft}
                        />
                    ))}

                    {liveRows.length === 0 &&
                        draftRows.length === 0 &&
                        loanIsActive && (
                            <Button
                                icon={<Add />}
                                label="Add first allocation"
                                alignSelf="start"
                                onClick={handleAddFirstAllocation}
                            />
                        )}
                </Box>
            )}

            {showHistory &&
                (retiredRows.length > 0 || deletedRows.length > 0) && (
                    <Box
                        gap="xsmall"
                        margin={{ top: "small" }}
                        pad={{ top: "small" }}
                        border={{ side: "top", color: "surface-border" }}
                    >
                        <Text size="small" weight="bold" color="dark-5">
                            History
                        </Text>
                        {retiredRows.map((row) => (
                            <HistoryRow
                                key={row.id}
                                row={row}
                                tone="retired"
                                funderName={funderName}
                                successor={successorOf(row)}
                                isAdmin={isAdmin}
                                onDelete={(id) => setConfirmingDeleteId(id)}
                            />
                        ))}
                        {deletedRows.map((row) => (
                            <HistoryRow
                                key={row.id}
                                row={row}
                                tone="deleted"
                                funderName={funderName}
                                isAdmin={isAdmin}
                                onRestore={handleRestore}
                                onHardDelete={(id) =>
                                    setConfirmingHardDeleteId(id)
                                }
                            />
                        ))}
                    </Box>
                )}

            <Box
                direction="row"
                justify="between"
                align="center"
                margin={{ top: "small" }}
                pad={{ top: "small" }}
                border={{ side: "top", color: "surface-border" }}
            >
                <SummaryBar
                    currentCents={currentTotalCents}
                    totalCents={loanAmountCents}
                />
                <Button
                    label="Save allocations"
                    {...ctaButtonProps}
                    disabled={!canSave || saving}
                    onClick={handleSave}
                />
            </Box>

            {confirmingDeleteId !== null && (
                <ConfirmDialog
                    title="Delete this allocation record?"
                    message="Only use this to correct a mistake entry - reallocating a live allocation to a different funder should be done via 'Reallocate', not by deleting it."
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmingDeleteId(null)}
                />
            )}
            {confirmingHardDeleteId !== null && (
                <ConfirmDialog
                    title="Permanently delete this allocation?"
                    message="This cannot be undone - the record will be removed completely, not just hidden."
                    confirmLabel="Delete permanently"
                    onConfirm={handleHardDelete}
                    onCancel={() => setConfirmingHardDeleteId(null)}
                />
            )}
        </Panel>
    );
};

export { AllocationEditor };
